/**
 * /app/reportes — SCRUM-549: módulo de Reportes, descargas XLS entre fechas.
 */
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Download, Loader2, FileSpreadsheet, PackageMinus, ShoppingCart } from "lucide-react";
import { CrudShellHeader, Panel } from "@/components/ds";
import { ClinicalPageShell } from "@/components/clinical";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useTenantContextSafe } from "@/lib/tenant-context";
import { clientRequestHeaders } from "@/lib/sm-api";
import { toast } from "sonner";

export const Route = createFileRoute("/app/reportes")({
  component: ReportesPage,
  head: () => ({ meta: [{ title: "Reportes — Structura Market" }] }),
});

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthIso(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

function ReportesPage() {
  const tid = useWorkspaceTenantId();
  const ctx = useTenantContextSafe();
  const entitled = ctx?.productFlags?.exports_csv === true;

  if (!entitled) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Requiere la feature exports_csv en tu plan.
      </div>
    );
  }

  return (
    <div>
      <CrudShellHeader title="Reportes" />
      <ClinicalPageShell offset="none">
        <div className="mx-auto w-full max-w-3xl space-y-4 p-4 pb-12">
          <p className="text-sm text-muted-foreground">
            Descargá información del negocio en XLS para un rango de fechas.
          </p>
          {tid && (
            <>
              <ReportCard
                tid={tid}
                icon={PackageMinus}
                title="Egresos por ventas entre fechas"
                description="Productos vendidos en el período: cantidad, costo e importe totales, y stock actual. No incluye ajustes ni movimientos manuales de stock."
                endpoint="/api/reports/sales-stock-egress"
                filenamePrefix="egresos-por-ventas"
              />
              <ReportCard
                tid={tid}
                icon={ShoppingCart}
                title="Ventas entre fechas"
                description="Todas las ventas del período con vendedor, cliente, métodos de pago y comprobante."
                endpoint="/api/reports/sales"
                filenamePrefix="ventas"
              />
            </>
          )}
        </div>
      </ClinicalPageShell>
    </div>
  );
}

function ReportCard({
  tid,
  icon: Icon,
  title,
  description,
  endpoint,
  filenamePrefix,
}: {
  tid: string;
  icon: typeof FileSpreadsheet;
  title: string;
  description: string;
  endpoint: string;
  filenamePrefix: string;
}) {
  const [from, setFrom] = useState(firstOfMonthIso());
  const [to, setTo] = useState(todayIso());
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    if (!from || !to || from > to) {
      toast.error("El rango de fechas es inválido");
      return;
    }
    setDownloading(true);
    try {
      const res = await fetch(`${endpoint}?from=${from}&to=${to}`, {
        credentials: "include",
        headers: clientRequestHeaders(tid),
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => "Error al generar el reporte");
        throw new Error(msg || "Error al generar el reporte");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filenamePrefix}-${from}_a_${to}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Reporte descargado");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al generar el reporte");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Panel title={title} description={description} actions={<Icon className="h-4 w-4 text-muted-foreground" />}>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Desde</Label>
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            className="mt-0 flex h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
          />
        </div>
        <div>
          <Label className="text-xs">Hasta</Label>
          <input
            type="date"
            value={to}
            min={from}
            max={todayIso()}
            onChange={(e) => setTo(e.target.value)}
            className="mt-0 flex h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
          />
        </div>
        <Button size="sm" onClick={download} disabled={downloading} className="gap-2">
          {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          Descargar XLS
        </Button>
      </div>
    </Panel>
  );
}
